<template>
  <v-card class="rounded-lg fill-height">
    <v-card-title class="pa-4">
      <v-row
        align="center"
        no-gutters
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('individual_insights.table.title') }}
          </div>
        </v-col>
        <v-col
          cols="auto"
          class="pl-2"
        >
          <variable-selector
            v-if="expandedVariables.length > 0"
            :variables="expandedVariables"
            :layout-variables.sync="selectedVariablesList"
            :excluded-variables="[]"
            :use-long-text="true"
            @update="$emit('variables-updated')"
          />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-data-table
        :headers="tableHeaders"
        :items="trendStats"
        :loading="loading"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        dense
      >
        <template v-slot:item="{ item }">
          <tr
            style="cursor: pointer;"
            @click="$emit('date-click', item.date)"
          >
            <td class="py-2">
              <span class="font-weight-medium text-body-2">
                {{ formatDate(item.date) }}
              </span>
            </td>
            <td
              v-for="variable in selectedVariables"
              :key="variableStatsKey(variable)"
              :style="isTagBreakdownVar(variable) ? 'min-width: 180px' : 'min-width: 100px'"
            >
              <template v-if="isTagBreakdownVar(variable)">
                <tag-breakdown-cell
                  :stats="item.stats"
                  :tag="getTagForVariable(variable)"
                  :stat-prefix="variableStatsPrefix(variable)"
                  :suffix="variable.suffix"
                />
              </template>
              <template v-else>
                <span
                  class="d-block"
                  :class="{ 'grey--text text--lighten-1': item.stats[variableStatsKey(variable)] == null }"
                >
                  {{ formatStatValue(item.stats[variableStatsKey(variable)], variable) }}
                </span>
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import TagBreakdownCell from './TagBreakdownCell.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import dayjs from 'dayjs'

export default {
  name: 'IndividualInsightsTrendTable',
  components: { TagBreakdownCell, VariableSelector },
  props: {
    trendStats: {
      type: Array,
      required: true
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    variables: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    expandedVariables() {
      const result = []
      this.variables.forEach(v => {
        result.push(v)
        if (v.suffix_agg && typeof v.suffix_agg === 'object') {
          Object.keys(v.suffix_agg).forEach(aggKey => {
            result.push({ ...v, aggregation: aggKey })
          })
        }
      })
      return result
    },
    selectedVariablesList: {
      get() {
        return this.selectedVariables
      },
      set(value) {
        this.$emit('update:selectedVariables', value)
      }
    },
    tableHeaders() {
      const headers = [
        { text: this.$t('individual_insights.table.date'), value: 'date', sortable: false, width: '140px' }
      ]

      this.selectedVariables.forEach(variable => {
        let text = variable.long_text || variable.text || variable.value
        if (variable.aggregation) {
          text += ` (${this.getAggregationLabel(variable.aggregation)})`
        }
        headers.push({
          text,
          value: this.variableStatsKey(variable),
          sortable: false,
          width: this.isTagBreakdownVar(variable) ? '180px' : '120px'
        })
      })

      return headers
    }
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format('MMM D, YYYY')
    },
    variableStatsKey(variable) {
      const varKey = variable.aggregation ? `${variable.value}_${variable.aggregation}` : variable.value
      return `${variable.category.toLowerCase()}.${varKey}`
    },
    variableStatsPrefix(variable) {
      return `${variable.category.toLowerCase()}.${variable.value}`
    },
    isTagBreakdownVar(variable) {
      return !!variable.aggregation
    },
    getTagForVariable(variable) {
      return {
        id: variable.aggregation,
        label: this.getAggregationLabel(variable.aggregation)
      }
    },
    getAggregationLabel(aggregation) {
      if (aggregation === 'group_id') return this.$t('insights.by_group')
      return aggregation.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    formatStatValue(value, variable) {
      if (value == null) return 'n/a'

      if (typeof value === 'object') {
        if (typeof value.total === 'number') {
          const formatted = Number.isInteger(value.total) ? value.total : Math.round(value.total * 100) / 100
          return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
        }
        return 'n/a'
      }

      if (typeof value === 'number') {
        const formatted = Number.isInteger(value) ? value : Math.round(value * 100) / 100
        return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
      }

      return value
    }
  }
}
</script>
